import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { DemoTourProvider } from './context/DemoTourContext';
import { Toaster } from './components/ui/sonner';
import PublicLayout from './layouts/PublicLayout';
import AppLayout from './layouts/AppLayout';

// Public Pages
import Home from './pages/Home';
import Login from './pages/Login';
import AdminLogin from './pages/AdminLogin';

// Apprentice Pages
import ApprenticeDashboard from './pages/apprentice/Dashboard';
import ApprenticeProfile from './pages/apprentice/Profile';
import PersonalityProfile from './pages/apprentice/PersonalityProfile';
import CareerPreferences from './pages/apprentice/CareerPreferences';
import Assessments from './pages/apprentice/Assessments';
import Booking from './pages/apprentice/Booking';
import SavedRoles from './pages/apprentice/SavedRoles';
import ResumeCompiler from './pages/apprentice/CVBuilder';
import Matches from './pages/apprentice/Matches';
import ApprenticeMessages from './pages/apprentice/Messages';
import FinalDoc from './pages/apprentice/FinalDoc';
import ApplyPage from './pages/apprentice/ApplyPage';
import Notifications from './pages/apprentice/Notifications';

// Counsellor Pages
import CounsellorLogin from './pages/CounsellorLogin';
import CounsellorDashboard from './pages/counsellor/Dashboard';
import CounsellorReportView from './pages/counsellor/ReportView';

// Employer Pages
import EmployerDashboard from './pages/employer/Dashboard';
import Candidates from './pages/employer/Candidates';
import PostRole from './pages/employer/PostRole';
import Pipeline from './pages/employer/Pipeline';
import Billing from './pages/employer/Billing';
import EmployerMessages from './pages/employer/Messages';

// Admin Pages
import AdminOverview from './pages/admin/Overview';
import AdminUsers from './pages/admin/Users';
import MatchingWeights from './pages/admin/MatchingWeights';
import Analytics from './pages/admin/Analytics';

// Placeholder pages
const PlaceholderPage = ({ title }) => (
  <div className="p-8">
    <div className="max-w-4xl mx-auto text-center">
      <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h1>
      <p className="text-slate-600">This page is under construction for the demo.</p>
    </div>
  </div>
);

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <DemoTourProvider>
          <Toaster position="top-right" />
          <Routes>
            {/* Public Routes */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/admin-login" element={<AdminLogin />} />
              <Route path="/counsellor-login" element={<CounsellorLogin />} />
              <Route path="/how-it-works" element={<PlaceholderPage title="How It Works" />} />
              <Route path="/for-apprentices" element={<PlaceholderPage title="For Apprentices" />} />
              <Route path="/for-employers" element={<PlaceholderPage title="For Employers" />} />
              <Route path="/pricing" element={<PlaceholderPage title="Pricing" />} />
              <Route path="/about" element={<PlaceholderPage title="About Us" />} />
              <Route path="/faq" element={<PlaceholderPage title="FAQ" />} />
              <Route path="/contact" element={<PlaceholderPage title="Contact" />} />
              <Route path="/privacy" element={<PlaceholderPage title="Privacy Policy" />} />
              <Route path="/terms" element={<PlaceholderPage title="Terms of Service" />} />
            </Route>

            {/* App Routes */}
            <Route element={<AppLayout />}>
              {/* Apprentice Routes */}
              <Route path="/apprentice/dashboard" element={<ApprenticeDashboard />} />
              <Route path="/apprentice/profile" element={<ApprenticeProfile />} />
              <Route path="/apprentice/cv" element={<ResumeCompiler />} />
              <Route path="/apprentice/personality" element={<PersonalityProfile />} />
              <Route path="/apprentice/career-preferences" element={<CareerPreferences />} />
              <Route path="/apprentice/career-discovery" element={<Navigate to="/apprentice/career-preferences" replace />} />
              <Route path="/apprentice/assessments" element={<Assessments />} />
              <Route path="/apprentice/preferences" element={<Navigate to="/apprentice/career-preferences" replace />} />
              <Route path="/apprentice/booking" element={<Booking />} />
              <Route path="/apprentice/final-doc" element={<FinalDoc />} />
              <Route path="/apprentice/apply" element={<ApplyPage />} />
              <Route path="/apprentice/notifications" element={<Notifications />} />
              <Route path="/apprentice/saved-roles" element={<SavedRoles />} />
              <Route path="/apprentice/matches" element={<Matches />} />
              <Route path="/apprentice/messages" element={<ApprenticeMessages />} />
              <Route path="/apprentice/settings" element={<PlaceholderPage title="Settings" />} />

              {/* Employer Routes */}
              <Route path="/employer/dashboard" element={<EmployerDashboard />} />
              <Route path="/employer/candidates" element={<Candidates />} />
              <Route path="/employer/post-role" element={<PostRole />} />
              <Route path="/employer/pipeline" element={<Pipeline />} />
              <Route path="/employer/messages" element={<EmployerMessages />} />
              <Route path="/employer/billing" element={<Billing />} />
              <Route path="/employer/settings" element={<PlaceholderPage title="Settings" />} />

              {/* Admin Routes */}
              <Route path="/admin/overview" element={<AdminOverview />} />
              <Route path="/admin/users" element={<AdminUsers />} />
              <Route path="/admin/matching-weights" element={<MatchingWeights />} />
              <Route path="/admin/analytics" element={<Analytics />} />
            </Route>

            {/* Counsellor Routes (own layout) */}
            <Route path="/counsellor/dashboard" element={<CounsellorDashboard />} />
            <Route path="/counsellor/report/:reportId" element={<CounsellorReportView />} />

            {/* Redirect */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </DemoTourProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
